import CheckIcon from "@mui/icons-material/Check";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { styled } from "@mui/material";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { KeyboardKey } from "_shared/_utilities/keyboard-keys";
import LoadingButton from "_shared/_views/loading/loading-button";
import EmailAddressInput from "_shared/email-addresses/_views/email-address-input";
import EmailAddress from "_shared/email-addresses/email-address";
import NameInput from "_shared/names/_views/name-input";
import Name from "_shared/names/name";
import {
  useAcceptUserEntityMemberInvitationMutation,
  useResendUserEntityMemberInvitationLinkMutation,
} from "legal-entities/invitations/member-invitations/email-based/_api/user-entity-member-invitation-api-service";
import UserEntityMemberInvitation, {
  InvalidUserEntityMemberInvitationError,
  InvitationForAnotherAccountError,
  InvitationLinkInvalidError,
  InvitationRequiresSignInError,
} from "legal-entities/invitations/member-invitations/email-based/user-entity-member-invitation";
import { InvitationStatus } from "legal-entities/invitations/statuses/invitation-status";
import RegistrationInfo, { InvalidRegistrationInfoError } from "legal-entities/registration-info/registration-info";
import { ownIndividualProfileRoute } from "marketplace/_views/profiles/own-individual-profile-route";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import type Session from "users/__session/session";
import { useSession } from "users/__session/session-context";
import PasswordInput, { PasswordIssues } from "users/auth/credentials/passwords/_views/password-input";
import Password from "users/auth/credentials/passwords/password";

const InvitationForm = styled(Container)(({ theme }) => ({
  color: theme.palette.common.white,
  padding: theme.spacing(2),
  position: "relative",
  width: "100%",
}));
const InvitationFormTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));
const InvitationFormInstructions = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.white,
  marginBottom: theme.spacing(2),
  "& a": {
    color: theme.palette.common.white,
    textDecoration: "underline",
  },
}));
const InvitationEmailInput = styled(EmailAddressInput)(({ theme }) => ({
  width: "100%",
  "& .MuiFilledInput-root": {
    backgroundColor: theme.palette.common.white,
  },
  "& .MuiFilledInput-root:hover": {
    backgroundColor: theme.palette.common.white,
  },
  "& .MuiFilledInput-root.Mui-focused": {
    backgroundColor: theme.palette.common.white,
  },
  "& .MuiFilledInput-root.Mui-disabled": {
    backgroundColor: theme.palette.common.white,
  },
}));
const InvitationNameInput = styled(NameInput)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  "& .MuiFilledInput-root": {
    backgroundColor: theme.palette.common.white,
  },
  "& .MuiFilledInput-root:hover": {
    backgroundColor: theme.palette.common.white,
  },
  "& .MuiFilledInput-root.Mui-focused": {
    backgroundColor: theme.palette.common.white,
  },
}));
const InvitationPasswordInput = styled(PasswordInput)(({ theme }) => ({
  "& .MuiFilledInput-root": {
    backgroundColor: theme.palette.common.white,
  },
  "& .MuiFilledInput-root:hover": {
    backgroundColor: theme.palette.common.white,
  },
  "& .MuiFilledInput-root.Mui-focused": {
    backgroundColor: theme.palette.common.white,
  },
}));
const InvitationAcceptButton = styled(LoadingButton)(({ theme }) => ({
  backgroundColor: "#E19941",
  color: theme.palette.common.white,
  marginTop: theme.spacing(2),
  "&.MuiButton-root": {
    color: theme.palette.common.white,
    "&:hover": {
      backgroundColor: "#FFB949",
    },
    "&.Mui-disabled": {
      color: "rgba(255, 255, 255, 0.3)",
      boxShadow: "none",
      backgroundColor: "rgba(255, 255, 255, 0.12)",
    },
  },
}));
// One column for every row of the form, so the email, the names and the passwords share a left
// edge and a width instead of each sizing itself. Centred in the form like its title, instructions and
// button: capped and left alone, the column hugged the left of a wide window under a centred page.
const FormElements = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginInline: "auto",
  maxWidth: theme.spacing(90),
}));

/**
 * Where a member goes once they have accepted: their own profile, open on the credentials field.
 *
 * Accepting is what creates their marketplace individual, empty but for their name, and nobody can
 * be added to one of the firm's teams until it lists their professional credentials (#5800). The
 * person who invited them is usually about to put them on one, so the profile is the first thing
 * worth their time — the dashboard would say the same in its queue, one click further away.
 *
 * Only for a firm's member. A client's in-house colleagues join a client team, which bills nobody
 * and asks nothing of their profile, so for them the profile is not the first thing worth their time.
 *
 * Read off the session after acceptance has replaced it, which is when it knows the new individual
 * and which side it views as. Without an individual, for a client's member, or in a restricted
 * onboarding session the profiles page would turn away, the dashboard as before.
 */
function destinationAfterAcceptance(session: Session): string {
  const individualId = session.user?.individualId?.value;
  const joinsAFirm = session.context?.viewingAsVendor === true;
  if (!individualId || !joinsAFirm || session.isRestrictedOnboarding) return "/dashboard";
  return ownIndividualProfileRoute(individualId, { promptForCredentials: true });
}

type InvitationAcceptanceFormError = string | PasswordIssues;

type InvitationAcceptanceFormErrors = {
  general?: string;
  email?: string;
  name?: string;
  password?: PasswordIssues;
};

type UserEntityMemberInvitationAcceptanceFormProps = {
  className?: string;
  invitation: UserEntityMemberInvitation;
  /** The acceptance token from the emailed link's query string. Absent from links sent before #5388. */
  acceptanceToken?: string;
};

export default function UserEntityMemberInvitationAcceptanceForm(
  props: Readonly<UserEntityMemberInvitationAcceptanceFormProps>,
) {
  const { acceptanceToken, className, invitation } = props;

  const [invitationName, setInvitationName] = useState<Name>(invitation.registrationInfo.name);
  const [invitationNameSource, setInvitationNameSource] = useState(invitation.registrationInfo.name);
  const [invitationPassword, setInvitationPassword] = useState<Password>();
  const [formErrors, setFormErrors] = useState<InvitationAcceptanceFormErrors>();
  const [saving, setSaving] = useState<boolean>(false);
  // Set when accepting found the invitee already has an account: they accept by signing into it.
  const [needsSignIn, setNeedsSignIn] = useState(false);
  // Set when the link cannot accept - it has no token, or the API refused the one it has.
  const [linkRefused, setLinkRefused] = useState(false);
  const [freshLinkSent, setFreshLinkSent] = useState(false);
  const invitationEmail = invitation?.registrationInfo.email;
  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [acceptUserEntityMemberInvitation] = useAcceptUserEntityMemberInvitationMutation();
  const [resendUserEntityMemberInvitationLink] = useResendUserEntityMemberInvitationLinkMutation();

  // Signed in, the session is what shows who is accepting, so there is nothing to type. Whether
  // it is the invitee's is the API's to decide; comparing addresses here only chooses what to offer.
  const signedInEmail = session.isLoggedIn ? session.user?.email?.value : undefined;
  const signedInAsInvitee =
    signedInEmail !== undefined && signedInEmail.toLowerCase() === invitationEmail?.value.toLowerCase();
  const linkUsable = Boolean(acceptanceToken) && !linkRefused;

  const displayedInvitationName = invitationNameSource === invitation.registrationInfo.name
    ? invitationName
    : invitation.registrationInfo.name;

  function rebuildInvitation(
    emailAddress?: EmailAddress,
    name?: Name,
    password?: Password,
    errors?: InvitationAcceptanceFormErrors,
  ): UserEntityMemberInvitation | undefined {
    if (!invitation || !formValid(errors) || !emailAddress || !name || !password) return;

    try {
      const invitationRegistrationInfo = RegistrationInfo.create({
        params: {
          name,
          email: emailAddress,
          password,
        },
        options: { silenceSchemaErrors: true },
      });
      const updatedInvitation = UserEntityMemberInvitation.create({
        userEntityInfo: invitation.userEntityInfo,
        registrationInfo: invitationRegistrationInfo,
        id: invitation.id,
      });
      return updatedInvitation;
    } catch (error) {
      if (error instanceof InvalidUserEntityMemberInvitationError || error instanceof InvalidRegistrationInfoError) {
        let errors: InvitationAcceptanceFormErrors = {};
        errors.general = error.message;
        return;
      }
      throw error;
    }
  }

  async function handleAcceptInvitation() {
    setSaving(true);

    try {
      const builtInvite = rebuildInvitation(invitationEmail, displayedInvitationName, invitationPassword);
      if (!builtInvite) throw new InvalidUserEntityMemberInvitationError("Invitation is invalid");

      await acceptUserEntityMemberInvitation({
        invitation: builtInvite,
        session,
        acceptance: { signedIn: false, token: acceptanceToken ?? "" },
      }).unwrap();
      navigate(destinationAfterAcceptance(session));
    } catch (error) {
      if (error instanceof InvitationRequiresSignInError) {
        setNeedsSignIn(true);
        return;
      }
      if (error instanceof InvitationLinkInvalidError) {
        setLinkRefused(true);
        return;
      }
      console.error(error);
      const message = (error as Error)?.message ?? "";
      if (message.includes("Zod")) {
        enqueueSnackbar("There was an error processing your invitation.", { variant: "error" });
      } else {
        enqueueSnackbar(message, { variant: "error" });
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleAcceptSignedIn() {
    setSaving(true);
    try {
      await acceptUserEntityMemberInvitation({ invitation, session, acceptance: { signedIn: true } }).unwrap();
      navigate(destinationAfterAcceptance(session));
    } catch (error) {
      const message = error instanceof InvitationForAnotherAccountError ? error.message : (error as Error)?.message;
      enqueueSnackbar(message || "There was an error accepting your invitation.", { variant: "error" });
      setSaving(false);
    }
  }

  async function handleSendFreshLink() {
    setSaving(true);
    try {
      await resendUserEntityMemberInvitationLink({ invitationId: invitation.id }).unwrap();
      setFreshLinkSent(true);
    } catch (error) {
      enqueueSnackbar((error as Error)?.message || "We could not send a fresh link. Please try again.", {
        variant: "error",
      });
    }
    setSaving(false);
  }

  async function handleSignOut() {
    await session.logout();
    // The page reads the session once; loading it again is what shows it signed out.
    globalThis.location.reload();
  }

  function signInToAccept() {
    // Back to this page afterwards, where the session will accept. The token is not carried: a
    // signed-in acceptance does not need it.
    navigate("/login", { state: { from: { pathname: location.pathname } } });
  }

  function formValid(errors?: InvitationAcceptanceFormErrors): boolean {
    errors ??= formErrors;

    return (
      invitationEmail !== undefined &&
      displayedInvitationName !== undefined &&
      invitationPassword !== undefined &&
      !errors?.general &&
      !errors?.email &&
      !errors?.name &&
      !errors?.password
    );
  }

  function updateFormErrors(type: keyof InvitationAcceptanceFormErrors, error?: InvitationAcceptanceFormError) {
    let errors: InvitationAcceptanceFormErrors = { ...formErrors };

    if (type === "password") {
      errors.password = error as PasswordIssues;
    } else if (type === "email") {
      errors.email = error as string;
    } else if (type === "name") {
      errors.name = error as string;
    } else {
      errors.general = error as string;
    }

    setFormErrors(errors);
    return errors;
  }

  function invitationAlreadyAcceptedMessage() {
    return (
      <InvitationForm className={className}>
        <InvitationFormTitle variant="h4">Invitation Already Accepted</InvitationFormTitle>
        <InvitationFormInstructions>
          The invitation has already been accepted.
          <br />
          If you believe this is in error please contact{" "}
          <a href="mailto:support@attorneyhub.com" target="_blank" rel="noreferrer">
            support@attorneyhub
          </a>
          .<br />
          You can close this tab
        </InvitationFormInstructions>
      </InvitationForm>
    );
  }

  if (!invitation) return null;
  if (invitation.status === InvitationStatus.Accepted) return invitationAlreadyAcceptedMessage();

  const entityName = invitation.userEntityInfo.entityName?.value;

  if (signedInEmail !== undefined && signedInAsInvitee) {
    return (
      <InvitationForm className={className}>
        <InvitationFormTitle variant="h4">Accept Invitation</InvitationFormTitle>
        <InvitationFormInstructions>
          You are signed in as {signedInEmail}. Accept to join {entityName}.
        </InvitationFormInstructions>
        <InvitationAcceptButton
          variant="contained"
          color="primary"
          disabled={saving}
          loading={saving}
          startIcon={<CheckIcon />}
          onClick={handleAcceptSignedIn}
        >
          Accept Invitation
        </InvitationAcceptButton>
      </InvitationForm>
    );
  }

  if (signedInEmail !== undefined) {
    return (
      <InvitationForm className={className}>
        <InvitationFormTitle variant="h4">This Invitation Is for Someone Else</InvitationFormTitle>
        <InvitationFormInstructions>
          This invitation to {entityName} was sent to {invitationEmail?.value}, but you are signed in as{" "}
          {signedInEmail}. Sign out, then open the invitation again.
        </InvitationFormInstructions>
        <InvitationAcceptButton variant="contained" loading={false} startIcon={<LogoutIcon />} onClick={handleSignOut}>
          Sign Out
        </InvitationAcceptButton>
      </InvitationForm>
    );
  }

  if (needsSignIn) {
    return (
      <InvitationForm className={className}>
        <InvitationFormTitle variant="h4">Sign In to Accept</InvitationFormTitle>
        <InvitationFormInstructions>
          {invitationEmail?.value} already has an Attorney Hub account. Sign in with it to accept this
          invitation to {entityName}.
        </InvitationFormInstructions>
        <InvitationAcceptButton variant="contained" loading={false} startIcon={<LoginIcon />} onClick={signInToAccept}>
          Sign In
        </InvitationAcceptButton>
      </InvitationForm>
    );
  }

  if (!linkUsable) {
    return (
      <InvitationForm className={className}>
        <InvitationFormTitle variant="h4">This Link Has Expired</InvitationFormTitle>
        <InvitationFormInstructions>
          {freshLinkSent
            ? `We've sent a fresh link to ${invitationEmail?.value}. Open it from that email to accept.`
            : `This link can no longer be used to accept. We can email a fresh one to ${invitationEmail?.value}.`}
        </InvitationFormInstructions>
        {!freshLinkSent && (
          <InvitationAcceptButton
            variant="contained"
            disabled={saving}
            loading={saving}
            startIcon={<EmailOutlinedIcon />}
            onClick={handleSendFreshLink}
          >
            Email Me a Fresh Link
          </InvitationAcceptButton>
        )}
      </InvitationForm>
    );
  }

  return (
    <InvitationForm className={className}>
      <InvitationFormTitle variant="h4">Accept Invitation</InvitationFormTitle>
      <InvitationFormInstructions>
        Enter the following to register as an administrator on Attorney Hub account for{" "}
        {invitation.userEntityInfo.entityName?.value}:
      </InvitationFormInstructions>
      <form>
        <FormElements>
          <InvitationEmailInput variant="filled" disabled initialValue={invitationEmail} />
          <InvitationNameInput
            variant="filled"
            initialValue={displayedInvitationName}
            onChange={(updatedName?: Name, error?: string) => {
              if (updatedName) {
                setInvitationNameSource(invitation.registrationInfo.name);
                setInvitationName(updatedName);
              }
              const errors = updateFormErrors("name", error);
              rebuildInvitation(invitationEmail, updatedName, invitationPassword, errors);
            }}
          />
          <InvitationPasswordInput
            variant="filled"
            confirm={true}
            password={invitationPassword}
            onChange={(updatedPassword?: Password, error?: PasswordIssues) => {
              if (updatedPassword) setInvitationPassword(updatedPassword);
              const errors = updateFormErrors("password", error);
              rebuildInvitation(invitationEmail, displayedInvitationName, updatedPassword, errors);
            }}
            onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
              if (event.key === KeyboardKey.Enter && formValid()) {
                void handleAcceptInvitation();
              }
            }}
          />
        </FormElements>
        <InvitationAcceptButton
          variant="contained"
          color="primary"
          disabled={saving || !formValid()}
          loading={saving}
          startIcon={<CheckIcon />}
          onClick={handleAcceptInvitation}
        >
          Accept Invitation
        </InvitationAcceptButton>
      </form>
    </InvitationForm>
  );
}
